import { useState, useEffect } from 'react';
import { getProjects, createProject, signOut, getCredits, archiveProject, approveEngagement, rejectEngagement } from '../lib/supabase';
import CreditsModal from '../components/CreditsModal';
import WhatsNewModal, { CURRENT_VERSION } from '../components/WhatsNewModal';

const STATUS_CONFIG = {
  draft:             { label: 'Draft',             color: '#8a96b0', bg: '#f0f2f8' },
  awaiting_approval: { label: 'Needs Approval',    color: '#c98a00', bg: '#fff8e6' },
  sampled:           { label: 'Sample Drawn',       color: '#c98a00', bg: '#fff8e6' },
  awaiting_scoring:  { label: 'Awaiting Scoring',   color: '#c98a00', bg: '#fff3d6' },
  complete:          { label: 'Complete',            color: '#1a5c3a', bg: '#e4f2eb' },
};

const APPROVAL_CONFIG = {
  pending:           { label: '',                    color: '#8a96b0' },
  awaiting_approval: { label: '⏳ Awaiting Approval', color: '#c98a00' },
  approved:          { label: '✓ Approved',           color: '#1a5c3a' },
  rejected:          { label: '✗ Rejected',            color: '#c03a1a' },
};

export default function DashboardPage({ session, profile, onOpen, onTeam, onAudit, onLogout }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newClient, setNewClient] = useState('');
  const [newYears, setNewYears] = useState('');
  const [newGroup, setNewGroup] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [credits, setCredits] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedProjects, setArchivedProjects] = useState([]);
  const [showCredits, setShowCredits] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // engagement pending rejection
  const [rejectNote, setRejectNote] = useState('');

  const teamId = profile?.team_id;
  const teamName = profile?.teams?.name || 'Your Team';

  useEffect(() => {
    if (teamId) {
      loadProjects();
      loadCredits();
    }
  }, [teamId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadProjects = async () => {
    setLoading(true);
    const [activeRes, archivedRes] = await Promise.all([
      getProjects(teamId, false),
      getProjects(teamId, true),
    ]);
    const active = activeRes.data || [];
    const all = archivedRes.data || [];
    setProjects(active);
    setArchivedProjects(all.filter(p => p.archived));
    setLoading(false);
  };

  const loadCredits = async () => {
    const { credits: c } = await getCredits(teamId);
    setCredits(c);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newClient.trim()) return;
    if (newClient.trim().length > 200) { setError('Client name must be 200 characters or fewer.'); return; }
    if (newGroup.trim().length > 100)  { setError('Client group must be 100 characters or fewer.'); return; }
    setCreating(true);
    setError('');
    const { data, error } = await createProject(teamId, session.user.id, {
      client_name: newClient.trim(),
      tax_years: newYears.trim(),
      client_group: newGroup.trim() || null,
      status: 'draft',
      approval_status: 'pending',
    });
    setCreating(false);
    if (error) {
      setError(error.message);
    } else {
      setShowNew(false);
      setNewClient('');
      setNewYears('');
      setProjects(prev => [data, ...prev]);
    }
  };

  const handleApprove = async (eng, e) => {
    e.stopPropagation();
    await approveEngagement(eng.id, session?.user?.id);
    loadProjects();
  };

  const handleReject = (eng, e) => {
    e.stopPropagation();
    setRejectNote('');
    setRejectTarget(eng);
  };

  const handleRejectConfirm = async () => {
    await rejectEngagement(rejectTarget.id, session?.user?.id, rejectNote);
    setRejectTarget(null);
    setRejectNote('');
    loadProjects();
  };

  const handleArchive = async (proj, e) => {
    e.stopPropagation(); // prevent opening the project
    await archiveProject(proj.id, !proj.archived);
    loadProjects();
  };

  const handleLogout = async () => {
    await signOut();
    onLogout();
  };

  const fmt = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={styles.page}>
      {/* ── Nav ── */}
      <div style={styles.nav}>
        <div style={styles.navLeft}>
          <img
            src={process.env.PUBLIC_URL + '/logo.png'}
            alt="The LAB"
            style={styles.navLogo}
            onError={e => { e.target.style.display = 'none'; }}
          />
          <div>
            <div style={styles.navTitle}>Sampling Platform</div>
            <div style={styles.navTeam}>{teamName}</div>
          </div>
        </div>
        <div style={styles.navRight}>
          {credits !== null && (
            <button style={styles.creditsBadge} onClick={() => setShowCredits(true)}>
              {credits} credit{credits !== 1 ? 's' : ''} remaining
            </button>
          )}
          {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
            <button style={styles.teamBtn} onClick={onTeam}>Team</button>
          )}
          {profile?.role === 'super_admin' && (
            <button style={{...styles.teamBtn, color:'#F5A800', borderColor:'rgba(245,168,0,0.4)'}} onClick={onAudit}>Auditor View</button>
          )}
          <div style={styles.navUser}>{profile?.full_name || session.user.email}</div>
          <button style={styles.logoutBtn} onClick={handleLogout}>Sign Out</button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={styles.body}>
        {/* Header row */}
        <div style={styles.headerRow}>
          <div>
            <div style={styles.eyebrow}>Rev. Proc. 2011-42 Compliant</div>
            <h1 style={styles.pageTitle}>Projects</h1>
          </div>
          <button style={styles.newBtn} onClick={() => setShowNew(true)}>
            + New Project
          </button>
        </div>

        {/* New project form */}
        {showNew && (
          <div style={styles.newCard}>
            <div style={styles.newCardTitle}>New Project</div>
            <form onSubmit={handleCreate} style={styles.newForm}>
              <div style={styles.newFields}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Client Name</label>
                  <input
                    style={styles.input}
                    value={newClient}
                    onChange={e => setNewClient(e.target.value)}
                    placeholder="Acme Corporation"
                    required
                    autoFocus
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>Tax Year(s)</label>
                  <input
                    style={styles.input}
                    value={newYears}
                    onChange={e => setNewYears(e.target.value)}
                    placeholder="2021, 2022, 2023"
                  />
                </div>
                <div style={{...styles.fieldGroup, gridColumn: '1 / -1'}}>
                  <label style={styles.label}>Taxpayer Group <span style={{color:'#8a96b0',fontWeight:400,textTransform:'none'}}>(optional — links projects for same taxpayer)</span></label>
                  <input
                    style={styles.input}
                    value={newGroup}
                    onChange={e => setNewGroup(e.target.value)}
                    placeholder="e.g. Acme Corp — use same name across years to group"
                  />
                </div>
              </div>
              {error && <div style={styles.errorBox}>{error}</div>}
              <div style={styles.newButtons}>
                <button type="submit" style={styles.createBtn} disabled={creating}>
                  {creating ? 'Creating…' : 'Create Project →'}
                </button>
                <button
                  type="button"
                  style={styles.cancelBtn}
                  onClick={() => { setShowNew(false); setError(''); }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        {/* No team warning */}
        {!teamId && (
          <div style={styles.warningBox}>
            ⚠ Your account is not assigned to a team yet. Contact your administrator to get access.
          </div>
        )}

        {/* Project list */}
        {loading ? (
          <div style={styles.loading}>Loading projects…</div>
        ) : projects.length === 0 ? (
          <div style={styles.empty}>
            <div style={styles.emptyIcon}>📋</div>
            <div style={styles.emptyTitle}>No projects yet</div>
            <div style={styles.emptySub}>Create your first project to get started.</div>
          </div>
        ) : (
          <>
          <div style={styles.projectList}>
            {projects.map(eng => {
              const st = STATUS_CONFIG[eng.status] || STATUS_CONFIG.draft;
              return (
                <div
                  key={eng.id}
                  style={styles.engCard}
                  onClick={() => onOpen(eng)}
                >
                  <div style={styles.engLeft}>
                    <div style={styles.engClient}>{eng.client_name}</div>
                    <div style={styles.engMeta}>
                      {eng.client_group && <span style={{color:'#F5A800',fontWeight:600}}>{eng.client_group}</span>}
                      {eng.client_group && <span style={styles.dot}>·</span>}
                      {eng.tax_years && <span>{eng.tax_years}</span>}
                      {eng.tax_years && <span style={styles.dot}>·</span>}
                      <span>Updated {fmt(eng.updated_at)}</span>
                    </div>
                  </div>
                  <div style={styles.engRight}>
                    <span style={{ ...styles.statusBadge, color: st.color, background: st.bg }}>
                      {st.label}
                    </span>
                    {eng.approval_status && eng.approval_status !== 'pending' && (
                      <span style={{ fontFamily:"'Roboto Mono',monospace", fontSize:10, fontWeight:600,
                        color: (APPROVAL_CONFIG[eng.approval_status]||APPROVAL_CONFIG.pending).color }}>
                        {(APPROVAL_CONFIG[eng.approval_status]||APPROVAL_CONFIG.pending).label}
                      </span>
                    )}
                    {(profile?.role === 'admin' || profile?.role === 'manager' || profile?.role === 'super_admin') &&
                      eng.approval_status === 'awaiting_approval' && (
                      <>
                        <button style={{...styles.archiveBtn,color:'#1a5c3a',borderColor:'#1a5c3a'}}
                          onClick={(e) => handleApprove(eng, e)}>✓ Approve</button>
                        <button style={{...styles.archiveBtn,color:'#c03a1a',borderColor:'#c03a1a'}}
                          onClick={(e) => handleReject(eng, e)}>✗ Reject</button>
                      </>
                    )}
                    {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
                      <button
                        style={styles.archiveBtn}
                        onClick={(e) => handleArchive(eng, e)}
                        title="Archive project"
                      >
                        Archive
                      </button>
                    )}
                    <span style={styles.engArrow}>→</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Archived projects toggle */}
          {archivedProjects.length > 0 && (
            <div style={{ marginTop: 24 }}>
              <button
                style={styles.archivedToggle}
                onClick={() => setShowArchived(v => !v)}
              >
                {showArchived ? '▲' : '▼'} {archivedProjects.length} archived project{archivedProjects.length !== 1 ? 's' : ''}
              </button>
              {showArchived && (
                <div style={{ ...styles.projectList, marginTop: 10, opacity: 0.7 }}>
                  {archivedProjects.map(eng => {
                    const st = STATUS_CONFIG[eng.status] || STATUS_CONFIG.draft;
                    return (
                      <div
                        key={eng.id}
                        style={{ ...styles.engCard, borderStyle: 'dashed' }}
                        onClick={() => onOpen(eng)}
                      >
                        <div style={styles.engLeft}>
                          <div style={styles.engClient}>{eng.client_name}</div>
                          <div style={styles.engMeta}>
                            {eng.tax_years && <span>{eng.tax_years}</span>}
                            {eng.tax_years && <span style={styles.dot}>·</span>}
                            <span>Archived · Updated {fmt(eng.updated_at)}</span>
                          </div>
                        </div>
                        <div style={styles.engRight}>
                          <span style={{ ...styles.statusBadge, color: st.color, background: st.bg }}>
                            {st.label}
                          </span>
                          {profile?.role === 'admin' && (
                            <button
                              style={{ ...styles.archiveBtn, color: '#1a5c3a', borderColor: '#1a5c3a' }}
                              onClick={(e) => handleArchive(eng, e)}
                              title="Unarchive project"
                            >
                              Unarchive
                            </button>
                          )}
                          <span style={styles.engArrow}>→</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
          </>
        )}
      </div>
      {showCredits && (
        <CreditsModal
          teamId={teamId}
          currentCredits={credits ?? 0}
          onClose={() => { setShowCredits(false); loadCredits(); }}
        />
      )}

      {showWhatsNew && <WhatsNewModal onClose={() => setShowWhatsNew(false)} />}

      {/* ── Rejection note modal ── */}
      {rejectTarget && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalCard}>
            <div style={styles.modalTitle}>Reject Engagement</div>
            <div style={styles.modalSub}>
              {rejectTarget.client_name}
              {rejectTarget.tax_years ? ` · ${rejectTarget.tax_years}` : ''}
            </div>
            <label style={styles.label}>Reason for rejection <span style={{color:'#8a96b0',fontWeight:400,textTransform:'none'}}>(optional)</span></label>
            <textarea
              style={styles.modalTextarea}
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Add a note for the practitioner…"
              rows={3}
              autoFocus
            />
            <div style={styles.newButtons}>
              <button style={{...styles.createBtn, background:'#c03a1a'}} onClick={handleRejectConfirm}>
                ✗ Confirm Rejection
              </button>
              <button style={styles.cancelBtn} onClick={() => setRejectTarget(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={styles.footer}>
        <span style={styles.footerVersion}>{CURRENT_VERSION}</span>
        <span style={styles.footerDot}>·</span>
        <button style={styles.footerLink} onClick={() => setShowWhatsNew(true)}>
          What's New
        </button>
        <span style={styles.footerDot}>·</span>
        <span style={styles.footerCopy}>© {new Date().getFullYear()} The LAB Business Solutions</span>
      </div>
    </div>
  );
}

const NAV_H = 64;

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f4f6fb',
    fontFamily: "'Montserrat', sans-serif",
  },
  nav: {
    height: NAV_H,
    background: '#0D2461',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 32px',
    position: 'sticky',
    top: 0,
    zIndex: 100,
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  navLogo: {
    height: '40px',
    objectFit: 'contain',
  },
  navTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '15px',
    color: 'white',
    lineHeight: 1.2,
  },
  navTeam: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: '0.06em',
  },
  navRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  navUser: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: 'rgba(255,255,255,0.65)',
  },
  creditsBadge: {
    padding: '6px 14px',
    background: 'rgba(245,168,0,0.15)',
    border: '1px solid rgba(245,168,0,0.4)',
    borderRadius: '4px',
    color: '#F5A800',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
    letterSpacing: '0.02em',
  },
  teamBtn: {
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: 'rgba(255,255,255,0.8)',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  logoutBtn: {
    padding: '6px 14px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: 'white',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '12px',
    fontWeight: 600,
    cursor: 'pointer',
  },
  body: {
    maxWidth: '860px',
    margin: '0 auto',
    padding: '40px 24px 80px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: '32px',
  },
  eyebrow: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '10px',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#F5A800',
    marginBottom: '4px',
  },
  pageTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '32px',
    color: '#0D2461',
    margin: 0,
  },
  newBtn: {
    padding: '12px 22px',
    background: '#F5A800',
    color: '#0D2461',
    border: 'none',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer',
  },
  newCard: {
    background: 'white',
    border: '1.5px solid #c8d0e0',
    borderRadius: '8px',
    padding: '28px',
    marginBottom: '24px',
  },
  newCardTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '13px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#0D2461',
    marginBottom: '20px',
  },
  newForm: { display: 'flex', flexDirection: 'column', gap: '16px' },
  newFields: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: '#5a6a8a',
  },
  input: {
    padding: '10px 12px',
    border: '1.5px solid #c8d0e0',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '14px',
    color: '#0D2461',
    outline: 'none',
  },
  errorBox: {
    background: '#fff0ee',
    border: '1px solid #e05a3a',
    borderRadius: '6px',
    padding: '10px 14px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#c03a1a',
  },
  newButtons: { display: 'flex', gap: '12px' },
  createBtn: {
    padding: '11px 22px',
    background: '#0D2461',
    color: '#F5A800',
    border: 'none',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '13px',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '11px 22px',
    background: '#f4f6fb',
    color: '#5a6a8a',
    border: '1.5px solid #c8d0e0',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 600,
    fontSize: '13px',
    cursor: 'pointer',
  },
  warningBox: {
    background: '#fff8e6',
    border: '1px solid #F5A800',
    borderRadius: '6px',
    padding: '14px 18px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#7a5c10',
    marginBottom: '24px',
  },
  loading: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '13px',
    color: '#8a96b0',
    padding: '40px 0',
    textAlign: 'center',
  },
  empty: {
    textAlign: 'center',
    padding: '80px 0',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '18px',
    color: '#0D2461',
    marginBottom: '8px',
  },
  emptySub: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#8a96b0',
  },
  projectList: { display: 'flex', flexDirection: 'column', gap: '10px' },
  engCard: {
    background: 'white',
    border: '1.5px solid #c8d0e0',
    borderRadius: '8px',
    padding: '18px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  engLeft: { flex: 1 },
  engClient: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    fontSize: '16px',
    color: '#0D2461',
    marginBottom: '4px',
  },
  engMeta: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
  },
  dot: { color: '#c8d0e0' },
  engRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '20px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.04em',
  },
  engArrow: {
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '18px',
    color: '#c8d0e0',
  },
  archiveBtn: {
    padding: '4px 10px',
    background: 'none',
    border: '1px solid #c8d0e0',
    borderRadius: '4px',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  footer: {
    padding: '20px 28px',
    borderTop: '1px solid #e8ecf5',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: 'white',
  },
  footerVersion: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    fontWeight: 700,
    color: '#0D2461',
    background: '#e8ecf5',
    padding: '2px 8px',
    borderRadius: 4,
  },
  footerDot: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#c8d0e0',
  },
  footerLink: {
    background: 'none',
    border: 'none',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#F5A800',
    fontWeight: 600,
    cursor: 'pointer',
    padding: 0,
    textDecoration: 'underline',
  },
  footerCopy: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '11px',
    color: '#8a96b0',
  },
  archivedToggle: {
    background: 'none',
    border: 'none',
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#8a96b0',
    cursor: 'pointer',
    padding: '4px 0',
    letterSpacing: '0.04em',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(13,36,97,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 200,
  },
  modalCard: {
    background: 'white',
    borderRadius: '10px',
    padding: '32px',
    width: '100%',
    maxWidth: '460px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 8px 32px rgba(13,36,97,0.18)',
  },
  modalTitle: {
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 800,
    fontSize: '16px',
    color: '#0D2461',
  },
  modalSub: {
    fontFamily: "'Roboto Mono', monospace",
    fontSize: '12px',
    color: '#8a96b0',
    marginTop: '-8px',
  },
  modalTextarea: {
    padding: '10px 12px',
    border: '1.5px solid #c8d0e0',
    borderRadius: '6px',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '14px',
    color: '#0D2461',
    resize: 'vertical',
    outline: 'none',
  },
};
